import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { useCan } from '../lib/auth';
import { fmtMoney } from '../lib/format';
import type { DocProfits, MarginLevel, ProfitPeriod, ProfitSummary, ProfitView } from '../lib/types';

const TONE: Record<MarginLevel, string> = { good: 'ok', warn: 'warn', weak: 'bad', loss: 'loss' };
const TITLE: Record<MarginLevel, string> = { good: 'ביעד או מעליו', warn: 'מתחת ליעד', weak: 'רווחיות נמוכה', loss: 'מתחת לעלות' };

const pct = (n: number) => `${n.toLocaleString('he-IL', { maximumFractionDigits: 1 })}%`;

/** Gross margin % as a coloured badge (green at target, yellow below it, red low, dark red below cost). */
export function MarginBadge({ margin, level, estimated, title }: { margin: number | null | undefined; level: MarginLevel | null | undefined; estimated?: boolean; title?: string }) {
  if (margin == null || !level) return <span className="faint">—</span>;
  return (
    <span className={`badge ${TONE[level]} tabular`} title={title ?? `${TITLE[level]}${estimated ? ' · משוער' : ''}`}>
      {pct(margin)}
    </span>
  );
}

/** Tooltip text for a document or period: profit in ₪ and how much of it was costed. */
export function profitTitle(p: ProfitView) {
  return [
    `רווח גולמי ${fmtMoney(p.profit)} מתוך ${fmtMoney(p.covered)}`,
    `עלות ידועה ל-${p.coverage}% מהסכום${p.coverage < 100 ? ' (שאר השורות: מוצר לא משויך או בלי עלות)' : ''}`,
    p.estimated ? 'משוער — חלק מהעלויות נקבעו אחרי המכירה' : '',
  ]
    .filter(Boolean)
    .join('\n');
}

export function ProfitBadge({ p }: { p: ProfitView | undefined }) {
  if (!p) return <span className="faint">—</span>;
  if (p.margin == null) return <span className="faint" title="אין שורות עם עלות ידועה">—</span>;
  return (
    <span title={profitTitle(p)}>
      <MarginBadge margin={p.margin} level={p.level} title={profitTitle(p)} />
      {p.coverage < 60 ? <span className="sub faint">{p.coverage}% מהסכום</span> : null}
    </span>
  );
}

/** Margins of documents on screen (only for users with the profit permission). */
export function useDocProfits(ids: number[], enabled = true) {
  const can = useCan();
  const key = ids.join(',');
  return useQuery({
    queryKey: ['profit', 'documents', key],
    queryFn: () => api<DocProfits>(`/api/profit/documents${qs({ ids: key })}`),
    enabled: enabled && can('profit.view') && ids.length > 0,
    placeholderData: keepPreviousData,
  });
}

export function useProfitSummary(range: { from?: string; to?: string } = {}) {
  const can = useCan();
  return useQuery({
    queryKey: ['profit', 'summary', range.from ?? '', range.to ?? ''],
    queryFn: () => api<ProfitSummary>(`/api/profit/summary${qs({ from: range.from || undefined, to: range.to || undefined })}`),
    enabled: can('profit.view'),
    placeholderData: keepPreviousData,
  });
}

/** One period: margin %, gross profit and coverage. */
export function ProfitKpi({ label, p, onClick, active }: { label: string; p: ProfitPeriod | undefined | null; onClick?: () => void; active?: boolean }) {
  const body = (
    <>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{!p ? <span className="skeleton" style={{ width: 70, height: 20, marginTop: 5 }} /> : <MarginBadge margin={p.margin} level={p.level} title={profitTitle(p)} />}</div>
      <div className="kpi-meta">{p ? (p.margin == null ? 'אין חשבוניות עם עלות' : `רווח ${fmtMoney(p.profit, { round: true })} · ${p.invoices} חשבוניות`) : null}</div>
    </>
  );
  return onClick ? (
    <button className={`kpi kpi-button ${active ? 'active' : ''}`} aria-pressed={active} onClick={onClick} title={p ? profitTitle(p) : undefined}>
      {body}
    </button>
  ) : (
    <div className="kpi" title={p ? profitTitle(p) : undefined}>{body}</div>
  );
}
